import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    createQuestion, updateQuestion, deleteQuestion,
    createAnswer, updateAnswer, deleteAnswer,
    reorderQuizQuestions,
} from '@/services/api/chatbot';
import type { ChatbotQuestion, ChatbotAnswer, QuestionType, AnswerTarget, AdTrigger } from '@/types/chatbot';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
    Plus, Trash2, MessageCircle, MousePointerClick, Type, ArrowRight, ExternalLink,
    Webhook, X, GripVertical, ChevronDown, ChevronUp,
} from 'lucide-react';
import {
    DndContext, closestCenter, KeyboardSensor, PointerSensor,
    useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates,
    verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Props {
    quizId: number;
    questions: ChatbotQuestion[];
}

const TYPE_META: Record<QuestionType, { icon: JSX.Element; label: string; accent: string; bg: string; }> = {
    text:   { icon: <MessageCircle className="w-3.5 h-3.5" />, label: 'Bloco do bot',  accent: 'text-slate-600',   bg: 'bg-slate-50 border-slate-200' },
    choice: { icon: <MousePointerClick className="w-3.5 h-3.5" />, label: 'Pergunta com botões', accent: 'text-emerald-700', bg: 'bg-white border-slate-200' },
    input:  { icon: <Type className="w-3.5 h-3.5" />, label: 'Pergunta de input',     accent: 'text-blue-700',    bg: 'bg-white border-slate-200' },
};

const TARGET_META: Record<AnswerTarget, { icon: JSX.Element; label: string; color: string; }> = {
    next_question: { icon: <ArrowRight className="w-3 h-3" />,     label: 'próxima',  color: 'bg-slate-100 text-slate-700' },
    redirect:      { icon: <ExternalLink className="w-3 h-3" />,   label: 'redirect', color: 'bg-blue-100 text-blue-700' },
    callback:      { icon: <Webhook className="w-3 h-3" />,        label: 'callback', color: 'bg-purple-100 text-purple-700' },
    end:           { icon: <X className="w-3 h-3" />,              label: 'fim',      color: 'bg-rose-100 text-rose-700' },
};

export default function QuestionList({ quizId, questions }: Props) {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const refresh = () => queryClient.invalidateQueries({ queryKey: ['chatbot-questions', quizId] });

    const [localOrder, setLocalOrder] = useState<ChatbotQuestion[] | null>(null);
    const displayed = localOrder || questions;

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const reorderMut = useMutation({
        mutationFn: (ids: number[]) => reorderQuizQuestions(quizId, ids),
        onSuccess: () => { setLocalOrder(null); refresh(); },
        onError: (e: any) => {
            setLocalOrder(null);
            toast({ variant: 'destructive', title: 'Erro ao reordenar',
                description: e.response?.data?.detail || e.message });
        },
    });

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx = displayed.findIndex(q => q.id === active.id);
        const newIdx = displayed.findIndex(q => q.id === over.id);
        if (oldIdx < 0 || newIdx < 0) return;
        const reordered = arrayMove(displayed, oldIdx, newIdx);
        setLocalOrder(reordered);
        reorderMut.mutate(reordered.map(q => q.id));
    };

    const addQuestionMut = useMutation({
        mutationFn: (type: QuestionType) => createQuestion({
            quiz_id: quizId,
            type,
            order_index: (questions.length || 0) + 1,
            title: type === 'text' ? 'Nova mensagem do bot' : 'Nova pergunta',
        }),
        onSuccess: async (newQ, type) => {
            if (type === 'text') {
                try {
                    await createAnswer({
                        question_id: newQ.id,
                        order_index: 1,
                        label: 'Continuar',
                        target_type: 'next_question',
                        ad_trigger: 'none',
                    });
                } catch {}
            }
            refresh();
        },
    });

    return (
        <div className="space-y-1">
            {displayed.length === 0 ? (
                <EmptyState onAdd={(t) => addQuestionMut.mutate(t)} />
            ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={displayed.map(q => q.id)} strategy={verticalListSortingStrategy}>
                        <div className="relative">
                            {/* linha vertical conectora */}
                            <div className="absolute left-[15px] top-3 bottom-3 w-px bg-slate-200 pointer-events-none" />
                            {displayed.map((q, idx) => (
                                <SortableItem key={q.id} question={q} index={idx + 1}
                                    allQuestions={displayed} onChange={refresh} />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            {/* Botoes de adicionar */}
            <div className="pt-4 grid grid-cols-3 gap-2">
                <AddCard icon={<MessageCircle className="w-4 h-4" />} label="Bloco de texto"
                    sub="Bolha do bot, avança sozinha"
                    onClick={() => addQuestionMut.mutate('text')}
                    accent="hover:border-slate-400 hover:bg-slate-50" />
                <AddCard icon={<MousePointerClick className="w-4 h-4" />} label="Pergunta com botões"
                    sub="Múltipla escolha"
                    onClick={() => addQuestionMut.mutate('choice')}
                    accent="hover:border-emerald-400 hover:bg-emerald-50" />
                <AddCard icon={<Type className="w-4 h-4" />} label="Pergunta de input"
                    sub="Texto / email / phone"
                    onClick={() => addQuestionMut.mutate('input')}
                    accent="hover:border-blue-400 hover:bg-blue-50" />
            </div>
        </div>
    );
}

function AddCard({ icon, label, sub, onClick, accent }:
    { icon: JSX.Element; label: string; sub: string; onClick: () => void; accent: string }) {
    return (
        <button onClick={onClick}
            className={`text-left p-3 border border-dashed rounded-lg transition ${accent}`}>
            <div className="flex items-center gap-2 text-sm font-medium">
                <Plus className="w-3 h-3" />
                {icon}
                {label}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">{sub}</div>
        </button>
    );
}

function EmptyState({ onAdd }: { onAdd: (t: QuestionType) => void }) {
    return (
        <div className="border border-dashed rounded-lg p-8 text-center bg-slate-50/50">
            <div className="text-sm font-medium text-slate-700 mb-1">Comece o seu quiz</div>
            <p className="text-xs text-slate-500 mb-4">Escolha um tipo de bloco abaixo pra criar a primeira mensagem.</p>
            <div className="grid grid-cols-3 gap-2 max-w-md mx-auto">
                <Button variant="outline" size="sm" onClick={() => onAdd('text')}>
                    <MessageCircle className="w-3 h-3 mr-1" />Bloco
                </Button>
                <Button variant="outline" size="sm" onClick={() => onAdd('choice')}>
                    <MousePointerClick className="w-3 h-3 mr-1" />Botões
                </Button>
                <Button variant="outline" size="sm" onClick={() => onAdd('input')}>
                    <Type className="w-3 h-3 mr-1" />Input
                </Button>
            </div>
        </div>
    );
}

function SortableItem(props:
    { question: ChatbotQuestion; index: number; allQuestions: ChatbotQuestion[]; onChange: () => void }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
        useSortable({ id: props.question.id });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : undefined,
        position: 'relative',
    };
    return (
        <div ref={setNodeRef} style={style} className="group/card flex items-stretch gap-2 py-1.5">
            {/* coluna esquerda: numero + handle */}
            <div className="relative w-8 flex flex-col items-center pt-3">
                <span className="relative z-10 flex items-center justify-center w-7 h-7 rounded-full bg-white border-2 border-slate-300 text-[11px] font-bold text-slate-600 group-hover/card:hidden">
                    {props.index}
                </span>
                <button
                    type="button"
                    className="hidden group-hover/card:flex items-center justify-center w-7 h-7 rounded-full bg-white border-2 border-slate-300 cursor-grab active:cursor-grabbing text-slate-500 hover:border-emerald-400 hover:text-emerald-600 touch-none"
                    title="Arraste pra reordenar"
                    {...attributes} {...listeners}>
                    <GripVertical className="w-3.5 h-3.5" />
                </button>
            </div>

            {/* card */}
            <div className="flex-1 min-w-0">
                <QuestionCard {...props} />
            </div>
        </div>
    );
}

function QuestionCard({ question, allQuestions, onChange }:
    { question: ChatbotQuestion; index: number; allQuestions: ChatbotQuestion[]; onChange: () => void; }) {
    const meta = TYPE_META[question.type];
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState(question.title);
    const [expanded, setExpanded] = useState(false);
    const titleRef = useRef<HTMLInputElement>(null);
    const { toast } = useToast();

    useEffect(() => setTitleDraft(question.title), [question.title]);
    useEffect(() => { if (editingTitle) titleRef.current?.focus(); }, [editingTitle]);

    const updateTitleMut = useMutation({
        mutationFn: (title: string) => updateQuestion(question.id, { title }),
        onSuccess: () => { onChange(); setEditingTitle(false); },
    });

    const deleteMut = useMutation({
        mutationFn: () => deleteQuestion(question.id),
        onSuccess: () => { onChange(); toast({ title: 'Pergunta deletada' }); },
    });

    const saveTitle = () => {
        const t = titleDraft.trim();
        if (!t || t === question.title) { setEditingTitle(false); setTitleDraft(question.title); return; }
        updateTitleMut.mutate(t);
    };

    return (
        <div className={`border rounded-lg ${meta.bg} overflow-hidden`}>
            {/* HEADER */}
            <div className="flex items-center gap-2 px-3 py-2.5">
                <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${meta.accent} bg-white`}>
                    {meta.icon}
                    <span className="ml-1">{meta.label}</span>
                </Badge>
                <div className="flex-1 min-w-0">
                    {editingTitle ? (
                        <Input
                            ref={titleRef}
                            value={titleDraft}
                            onChange={(e) => setTitleDraft(e.target.value)}
                            onBlur={saveTitle}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') saveTitle();
                                if (e.key === 'Escape') { setEditingTitle(false); setTitleDraft(question.title); }
                            }}
                            className="h-7 text-sm"
                        />
                    ) : (
                        <button
                            onClick={() => setEditingTitle(true)}
                            className="text-sm font-medium text-left w-full truncate hover:text-emerald-700"
                            title="Clique pra editar">
                            {question.title || <span className="italic text-slate-400">sem título</span>}
                        </button>
                    )}
                </div>
                <Button size="sm" variant="ghost" onClick={() => setExpanded(!expanded)}
                    className="text-slate-500 hover:text-slate-700">
                    {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => deleteMut.mutate()}
                    className="text-slate-400 hover:text-red-600">
                    <Trash2 className="w-3.5 h-3.5" />
                </Button>
            </div>

            {/* CONTEUDO: ramos por tipo */}
            {question.type === 'text' ? (
                <TextBlockRow question={question} allQuestions={allQuestions} onChange={onChange} expanded={expanded} />
            ) : (
                <AnswersRow question={question} allQuestions={allQuestions} onChange={onChange} expanded={expanded} />
            )}

            {/* CONFIG AVANCADA (so quando expandido) */}
            {expanded && (
                <AdvancedSettings question={question} onChange={onChange} />
            )}
        </div>
    );
}

// ============ CONTEUDO PARA type=text ============
function TextBlockRow({ question, allQuestions, onChange }:
    { question: ChatbotQuestion; allQuestions: ChatbotQuestion[]; onChange: () => void; expanded: boolean }) {
    const existing = (question.answers && question.answers[0]) || null;
    const [nextId, setNextId] = useState<number | ''>(existing?.next_question_id || '');
    useEffect(() => setNextId(existing?.next_question_id || ''), [existing?.next_question_id]);
    const { toast } = useToast();

    const ensureAnswerMut = useMutation({
        mutationFn: async (targetId: number | '') => {
            if (existing) {
                return updateAnswer(existing.id, {
                    target_type: targetId ? 'next_question' : 'end',
                    next_question_id: targetId || undefined,
                });
            }
            return createAnswer({
                question_id: question.id,
                order_index: 1,
                label: 'Continuar',
                target_type: targetId ? 'next_question' : 'end',
                next_question_id: targetId || undefined,
                ad_trigger: 'none',
            });
        },
        onSuccess: () => { onChange(); toast({ title: 'Salvo' }); },
    });

    const onChangeTarget = (val: string) => {
        const v = Number(val) || '' as const;
        setNextId(v);
        ensureAnswerMut.mutate(v);
    };

    return (
        <div className="px-3 pb-2.5 flex items-center gap-2 text-xs">
            <ArrowRight className="w-3 h-3 text-slate-400" />
            <span className="text-slate-500">após esse bloco vai pra</span>
            <select value={nextId}
                onChange={(e) => onChangeTarget(e.target.value)}
                className="flex-1 min-w-0 bg-white border rounded px-2 py-1 text-xs">
                <option value="">— finalizar —</option>
                {allQuestions.filter(q => q.id !== question.id).map(q => (
                    <option key={q.id} value={q.id}>
                        {q.title}
                    </option>
                ))}
            </select>
        </div>
    );
}

// ============ CONTEUDO PARA type=choice / input ============
function AnswersRow({ question, allQuestions, onChange }:
    { question: ChatbotQuestion; allQuestions: ChatbotQuestion[]; onChange: () => void; expanded: boolean }) {
    const [editingAnswerId, setEditingAnswerId] = useState<number | null>(null);

    const createMut = useMutation({
        mutationFn: () => createAnswer({
            question_id: question.id,
            order_index: (question.answers?.length || 0) + 1,
            label: 'Nova resposta',
            target_type: 'next_question',
            ad_trigger: 'none',
        }),
        onSuccess: (a) => { onChange(); setEditingAnswerId(a.id); },
    });

    return (
        <div className="px-3 pb-3 space-y-1.5">
            {(question.answers || []).map(a => (
                <AnswerRow key={a.id} answer={a} allQuestions={allQuestions} onChange={onChange}
                    isEditing={editingAnswerId === a.id}
                    onEditToggle={(open) => setEditingAnswerId(open ? a.id : null)} />
            ))}
            <button onClick={() => createMut.mutate()}
                className="w-full px-3 py-1.5 text-xs text-slate-500 border border-dashed rounded hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition">
                <Plus className="w-3 h-3 inline mr-1" />Adicionar resposta
            </button>
        </div>
    );
}

function AnswerRow({ answer, allQuestions, onChange, isEditing, onEditToggle }:
    { answer: ChatbotAnswer; allQuestions: ChatbotQuestion[]; onChange: () => void;
      isEditing: boolean; onEditToggle: (open: boolean) => void }) {
    const targetMeta = TARGET_META[answer.target_type];

    const targetSummary = () => {
        if (answer.target_type === 'next_question' && answer.next_question_id) {
            const q = allQuestions.find(x => x.id === answer.next_question_id);
            return q ? q.title.substring(0, 32) : '';
        }
        if (answer.target_type === 'redirect') return answer.redirect_url || '(URL faltando)';
        if (answer.target_type === 'callback') return answer.callback_url || '(URL faltando)';
        if (answer.target_type === 'end') return 'finaliza';
        return '';
    };

    const deleteMut = useMutation({
        mutationFn: () => deleteAnswer(answer.id),
        onSuccess: onChange,
    });

    return (
        <div className="group/row">
            <button
                onClick={() => onEditToggle(!isEditing)}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 bg-white border rounded text-sm hover:border-emerald-400 transition text-left">
                <span className="font-medium flex-1 truncate">{answer.label}</span>
                <Badge variant="outline" className={`text-[10px] ${targetMeta.color} border-0`}>
                    {targetMeta.icon}
                    <span className="ml-1">{targetMeta.label}</span>
                </Badge>
                {answer.ad_trigger !== 'none' && (
                    <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">
                        {answer.ad_trigger}
                    </Badge>
                )}
                <span className="text-[10px] text-slate-400 truncate max-w-[140px] hidden sm:inline">
                    → {targetSummary()}
                </span>
                <Trash2
                    className="w-3 h-3 text-slate-300 hover:text-red-600 opacity-0 group-hover/row:opacity-100"
                    onClick={(e) => { e.stopPropagation(); deleteMut.mutate(); }} />
            </button>
            {isEditing && (
                <div className="mt-1 mb-2">
                    <AnswerEditor answer={answer} allQuestions={allQuestions}
                        onClose={() => onEditToggle(false)} onChange={onChange} />
                </div>
            )}
        </div>
    );
}

function AnswerEditor({ answer, allQuestions, onClose, onChange }:
    { answer: ChatbotAnswer; allQuestions: ChatbotQuestion[]; onClose: () => void; onChange: () => void }) {
    const { toast } = useToast();
    const [form, setForm] = useState({
        label: answer.label,
        value: answer.value || '',
        target_type: answer.target_type,
        next_question_id: answer.next_question_id || undefined,
        redirect_url: answer.redirect_url || '',
        callback_url: answer.callback_url || '',
        ad_trigger: answer.ad_trigger,
        reward_overlay_title: answer.reward_overlay_title || '',
        reward_overlay_subtitle: answer.reward_overlay_subtitle || '',
        reward_overlay_button: answer.reward_overlay_button || '',
    });
    const saveMut = useMutation({
        mutationFn: () => updateAnswer(answer.id, form),
        onSuccess: () => { onChange(); onClose(); toast({ title: 'Resposta salva' }); },
    });

    return (
        <div className="bg-slate-50 border rounded-md p-3 space-y-2.5 text-sm">
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <Label>Label do botão</Label>
                    <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
                </div>
                <div>
                    <Label>Value (interno)</Label>
                    <Input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
                </div>
                <div>
                    <Label>Ação ao clicar</Label>
                    <select value={form.target_type}
                        onChange={(e) => setForm({ ...form, target_type: e.target.value as AnswerTarget })}
                        className="w-full border rounded px-2 py-1.5 text-sm h-9">
                        <option value="next_question">Próxima pergunta</option>
                        <option value="redirect">Redirect (URL)</option>
                        <option value="callback">Callback (URL decide)</option>
                        <option value="end">Finaliza chat</option>
                    </select>
                </div>
                <div>
                    <Label>Disparar anúncio</Label>
                    <select value={form.ad_trigger}
                        onChange={(e) => setForm({ ...form, ad_trigger: e.target.value as AdTrigger })}
                        className="w-full border rounded px-2 py-1.5 text-sm h-9">
                        <option value="none">Nenhum</option>
                        <option value="interstitial">Interstitial</option>
                        <option value="reward">Reward</option>
                    </select>
                </div>
            </div>

            {form.target_type === 'next_question' && (
                <div>
                    <Label>Próxima pergunta</Label>
                    <select value={form.next_question_id || ''}
                        onChange={(e) => setForm({ ...form, next_question_id: Number(e.target.value) || undefined })}
                        className="w-full border rounded px-2 py-1.5 text-sm h-9">
                        <option value="">— selecione —</option>
                        {allQuestions.filter(q => q.id !== answer.question_id).map(q => (
                            <option key={q.id} value={q.id}>{q.title}</option>
                        ))}
                    </select>
                </div>
            )}

            {form.target_type === 'redirect' && (
                <div>
                    <Label>URL de redirect</Label>
                    <Input value={form.redirect_url}
                        onChange={(e) => setForm({ ...form, redirect_url: e.target.value })}
                        placeholder="https://oferta.com?kw={keyword}&country={country}" />
                    <p className="text-[10px] text-slate-500 mt-1">
                        Interpola variáveis de tracking_params definidas no tema do chat.
                    </p>
                </div>
            )}

            {form.target_type === 'callback' && (
                <div>
                    <Label>URL de callback</Label>
                    <Input value={form.callback_url}
                        onChange={(e) => setForm({ ...form, callback_url: e.target.value })}
                        placeholder="https://api.suaapp.com/quiz-step" />
                </div>
            )}

            {form.ad_trigger === 'reward' && (
                <div className="border rounded-md bg-amber-50 border-amber-200 p-2.5 space-y-2">
                    <div className="text-[10px] font-semibold text-amber-900 uppercase tracking-wide">
                        Overlay do Reward (antes do anúncio)
                    </div>
                    <Input value={form.reward_overlay_title}
                        onChange={(e) => setForm({ ...form, reward_overlay_title: e.target.value })}
                        placeholder="Título: A chance do seu empréstimo começa aqui" />
                    <Input value={form.reward_overlay_subtitle}
                        onChange={(e) => setForm({ ...form, reward_overlay_subtitle: e.target.value })}
                        placeholder="Subtítulo: Escolha valores entre R$500 e R$3.500..." />
                    <Input value={form.reward_overlay_button}
                        onChange={(e) => setForm({ ...form, reward_overlay_button: e.target.value })}
                        placeholder="Texto do botão: Liberar recomendação" />
                </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
                <Button size="sm" variant="ghost" onClick={onClose}>Cancelar</Button>
                <Button size="sm" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                    Salvar
                </Button>
            </div>
        </div>
    );
}

// ============ CONFIG AVANCADA (body, image, input options, typing delay) ============
function AdvancedSettings({ question, onChange }:
    { question: ChatbotQuestion; onChange: () => void }) {
    const [form, setForm] = useState({
        body: question.body || '',
        image_url: question.image_url || '',
        input_type: question.input_type || 'text',
        input_placeholder: question.input_placeholder || '',
        input_validation_regex: question.input_validation_regex || '',
        typing_delay_ms: question.typing_delay_ms || 0,
    });
    const { toast } = useToast();
    const saveMut = useMutation({
        mutationFn: () => updateQuestion(question.id, form),
        onSuccess: () => { onChange(); toast({ title: 'Configuração salva' }); },
    });

    return (
        <div className="px-3 pb-3 border-t pt-3 bg-slate-50/40 space-y-2 text-sm">
            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                Configurações avançadas
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                    <Label>Body (texto adicional - 2ª bolha)</Label>
                    <Textarea value={form.body}
                        onChange={(e) => setForm({ ...form, body: e.target.value })} rows={2} />
                </div>
                <div className="col-span-2">
                    <Label>Imagem (URL)</Label>
                    <Input value={form.image_url}
                        onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                        placeholder="https://..." />
                </div>
                <div>
                    <Label>Delay "digitando" (segundos)</Label>
                    <select
                        value={Math.round((form.typing_delay_ms || 0) / 1000)}
                        onChange={(e) => setForm({ ...form, typing_delay_ms: Number(e.target.value) * 1000 })}
                        className="w-full border rounded px-2 py-1.5 text-sm h-9">
                        {Array.from({ length: 11 }, (_, i) => (
                            <option key={i} value={i}>
                                {i === 0 ? 'instantâneo' : `${i} ${i === 1 ? 'segundo' : 'segundos'}`}
                            </option>
                        ))}
                    </select>
                </div>
                {question.type === 'input' && (
                    <>
                        <div>
                            <Label>Tipo de input</Label>
                            <select value={form.input_type}
                                onChange={(e) => setForm({ ...form, input_type: e.target.value })}
                                className="w-full border rounded px-2 py-1.5 text-sm h-9">
                                <option value="text">text</option>
                                <option value="email">email</option>
                                <option value="phone">phone</option>
                                <option value="number">number</option>
                            </select>
                        </div>
                        <div className="col-span-2">
                            <Label>Placeholder</Label>
                            <Input value={form.input_placeholder}
                                onChange={(e) => setForm({ ...form, input_placeholder: e.target.value })} />
                        </div>
                        <div className="col-span-2">
                            <Label>Regex de validação</Label>
                            <Input value={form.input_validation_regex}
                                onChange={(e) => setForm({ ...form, input_validation_regex: e.target.value })}
                                placeholder="^[^@]+@[^@]+\\.[^@]+$" />
                        </div>
                    </>
                )}
            </div>
            <div className="flex justify-end">
                <Button size="sm" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                    Salvar configurações
                </Button>
            </div>
        </div>
    );
}

function Label({ children }: { children: React.ReactNode }) {
    return <label className="text-[10px] font-medium text-slate-600 block mb-1 uppercase tracking-wide">{children}</label>;
}
